/**
 * WALIDATOR ZDALNEJ KONFIGURACJI SEZONOWEJ.
 *
 * <b>Zasada.</b> Wejście jest `unknown` i pochodzi z sieci. Wyjście jest
 * albo poprawnym motywem, albo `null`. Nie ma trzeciej możliwości i nie
 * ma ścieżki, którą treść z sieci trafia do DOM inaczej niż jako adres
 * obrazka w atrybucie `src`.
 *
 * <b>Dlaczego walidacja jest po stronie klienta, skoro backend też
 * waliduje.</b> Bo klient jest tym, co stoi na telefonie użytkownika.
 * Backend może zostać źle wdrożony, pomylić wersję kontraktu albo zostać
 * podmieniony — a wtedy jedyną obroną jest to, że aplikacja nie umie
 * wykonać niczego, czego nie zna z góry.
 *
 * <b>Fail-closed (OWNER 2026-09-29, „remote skins bez aktualizacji").</b>
 * Motyw rysuje się W CAŁOŚCI albo WCALE. Nieznane miejsce, efekt, rodzaj
 * zasobu, zły adres, za dużo zasobów, nieczytelna paleta, wymagany silnik
 * nowszy niż ten w aplikacji — każde z nich daje `null`, czyli zwykły
 * interfejs. „Połowa skórki" (scena bez hero, pasek bez palety) wyglądałaby
 * jak błąd aplikacji. Wyjątek: parametry liczbowe są PRZYCINANE do zakresów
 * (to normalizacja, nie błąd). Nieznane POLA najwyższego poziomu są
 * ignorowane — nie niosą niczego, co aplikacja umiałaby wykonać.
 */

import { KontekstSceny, walidujScene } from './scena.walidator';
import { scenaZDekoracji } from './scena.adapter';
import { Scena, UNIWERSALNY_SILNIK, WERSJA_SCHEMATU } from './scena.model';
import {
  Dekoracja,
  MAX_ZASOBOW,
  MAX_ZNAKOW_PASKA,
  MIEJSCA,
  MIN_KONTRAST_POMOCNICZEGO,
  MIN_KONTRAST_TEKSTU,
  Miejsce,
  MotywSezonowy,
  Paleta,
  OGRANICZENIA,
  PasekWiadomosci,
  TYPOGRAFIE,
  Typografia,
  PRESETY,
  Preset,
  RODZAJE_ZASOBU,
  RaportWalidacji,
  RodzajZasobu,
  STANY_MOTYWU,
  STRATEGIA_AWARYJNA,
  StanMotywu,
  WERSJA_SILNIKA,
  ZDOLNOSCI_SILNIKA,
  WynikWalidacji,
  ZAKRESY,
  Zasob,
} from './kontrakt';

/**
 * DOZWOLONE POCHODZENIE ZASOBÓW.
 *
 * Adres spoza tej listy jest odrzucany, nawet jeśli jest po HTTPS.
 * To jest ochrona przed „backend podaje cudzy adres" — czyli przed
 * sytuacją, w której nasza aplikacja pobiera obrazek z serwera, którego
 * nikt u nas nie zatwierdził.
 */
/**
 * Dopasowanie jest sufiksowe Z KROPKĄ (`host === z || host.endsWith('.' + z)`),
 * więc wpis `tuszama.pl` obejmuje każdą poddomenę (`cdn.`, `static.`,
 * `kierowcy.` …) i NIE obejmuje `eviltuszama.pl` ani `tuszama.pl.zle.example`.
 * Jawne wpisy poddomen niczego nie dodawały — były trzema różnymi zapisami
 * tej samej reguły (D-07). Każdy wpis ma być osobną domeną, nie poddomeną
 * innego wpisu; pilnuje tego test w `walidator.spec.ts`. Nowe pochodzenie
 * spoza `tuszama.pl` (DEP-09) dopisuje się tu jako osobny wpis.
 */
export const DOZWOLONE_ZRODLA: readonly string[] = ['tuszama.pl'];

/** Rozszerzenia, które umiemy narysować w `<img>`. */
const DOZWOLONE_FORMATY: readonly string[] = [
  '.png',
  '.webp',
  '.avif',
  '.svg',
  '.gif',
];

/** Sufit rozmiaru pojedynczego zasobu — 512 kB. */
export const MAX_BAJTOW_ZASOBU = 512 * 1024;

/** Sufit liczby dekoracji w jednym motywie. */
export const MAX_DEKORACJI = 16;

const jestObiektem = (x: unknown): x is Record<string, unknown> =>
  typeof x === 'object' && x !== null && !Array.isArray(x);

const tekst = (x: unknown): string | null =>
  typeof x === 'string' && x.trim() ? x.trim() : null;

/** Kolor wyłącznie w postaci `#rrggbb` — nic, co CSS mógłby zinterpretować inaczej. */
const kolor = (x: unknown): string | null => {
  const t = tekst(x);
  return t && /^#[0-9a-f]{6}$/i.test(t) ? t.toLowerCase() : null;
};

/**
 * Żółty pasek okazji. Tekst to ZWYKŁY TEKST: bez znaków sterujących, bez
 * znaczników (i tak trafia przez interpolację, nigdy jako HTML), przycięty
 * do `MAX_ZNAKOW_PASKA`. Brak tekstu = brak paska okazji (zwykły pasek).
 */
export function walidujPasek(
  x: unknown,
  zasoby?: ReadonlyMap<string, Zasob>,
): PasekWiadomosci | null {
  if (!jestObiektem(x)) return null;
  const surowy = tekst(x['text'] ?? x['tekst']);
  if (!surowy) return null;
  // eslint-disable-next-line no-control-regex
  const czysty = surowy.replace(/[\u0000-\u001f\u007f<>]/g, '').trim();
  if (!czysty) return null;
  // IKONA (silnik 2): identyfikator zasobu z `assets`, który przeszedł już
  // walidację (pochodzenie, format, rozmiar). Podana, ale nieistniejąca albo
  // bez pliku = pasek nieprawidłowy (fail-closed: cały motyw odpada).
  let ikona: string | null = null;
  const idIkony = tekst(x['icon'] ?? x['iconAsset'] ?? x['iconAssetId'] ?? x['ikona']);
  if (idIkony) {
    const z = zasoby?.get(idIkony);
    if (!z || !z.url) return null;
    ikona = z.url;
  }
  const wyrSurowe = tekst(x['emphasis']);
  const wyroznienie = wyrSurowe ? wyrSurowe.toUpperCase() : 'NONE';
  if (wyroznienie !== 'NONE' && wyroznienie !== 'BOLD' && wyroznienie !== 'UPPERCASE') return null;
  return {
    tekst: [...czysty].slice(0, MAX_ZNAKOW_PASKA).join(''),
    tlo: kolor(x['background'] ?? x['backgroundColor'] ?? x['tlo']),
    kolorTekstu: kolor(x['textColor'] ?? x['kolorTekstu']),
    ikona,
    wyroznienie,
  };
}

/** Typografia z zamkniętej listy (`UPPERCASE`); cokolwiek innego = brak zmiany. */
export function walidujTypografie(x: unknown): Typografia | null {
  const t = tekst(
    jestObiektem(x) ? (x['textTransform'] ?? x['transform']) : x,
  );
  const w = t ? t.toUpperCase() : null;
  if (w === 'NONE') return null;
  return w && (TYPOGRAFIE as readonly string[]).includes(w)
    ? (w as Typografia)
    : null;
}

/** Luminancja względna `#rrggbb` (WCAG 2.x). */
function luminancja(hex: string): number {
  const kanal = (i: number): number => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * kanal(1) + 0.7152 * kanal(3) + 0.0722 * kanal(5);
}

/** Kontrast dwóch kolorów `#rrggbb` (1–21). */
export function kontrast(a: string, b: string): number {
  const [j, c] = [luminancja(a), luminancja(b)].sort((x, y) => y - x);
  return (j + 0.05) / (c + 0.05);
}

/**
 * Paleta ekranu menu. Wszystkie pięć ról musi być poprawnym `#rrggbb`,
 * a tekst musi być czytelny na tle i na karcie — inaczej `null` (barwy
 * aplikacji). Częściowej palety nie ma: tło bez koloru tekstu to przepis
 * na czarny tekst na czarnym tle.
 */
export function walidujPalete(x: unknown): Paleta | null {
  if (!jestObiektem(x)) return null;
  const tlo = kolor(x['background'] ?? x['tlo']);
  const karta = kolor(x['surface'] ?? x['karta']);
  const tekstGl = kolor(x['text'] ?? x['tekst']);
  const pomocniczy = kolor(x['muted'] ?? x['pomocniczy']);
  const linia = kolor(x['line'] ?? x['linia']);
  if (!tlo || !karta || !tekstGl || !pomocniczy || !linia) return null;
  if (
    kontrast(tekstGl, tlo) < MIN_KONTRAST_TEKSTU ||
    kontrast(tekstGl, karta) < MIN_KONTRAST_TEKSTU ||
    kontrast(pomocniczy, karta) < MIN_KONTRAST_POMOCNICZEGO
  ) {
    return null;
  }
  return { tlo, karta, tekst: tekstGl, pomocniczy, linia };
}

/**
 * Czas z kontraktu na milisekundy UTC.
 *
 * `Date.parse` na łańcuchu ISO ze strefą daje moment w czasie, a nie
 * „godzinę na ścianie". To jest cała semantyka strefowa, jakiej tu
 * potrzeba: backend podaje moment, klient go porównuje z `Date.now()`.
 * Łańcuch BEZ strefy jest odrzucany — „2026-10-31T00:00" znaczy co
 * innego w Warszawie i w Los Angeles, a zgadywanie byłoby błędem
 * cichym, nie głośnym.
 */
export function czasNaMs(x: unknown): number | null {
  const s = tekst(x);
  if (!s) return null;
  if (!/(?:Z|[+-]\d{2}:?\d{2})$/i.test(s)) return null;
  const ms = Date.parse(s);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Czy adres wolno pobrać. Zwraca powód odrzucenia albo `null`.
 *
 * WŁASNE POCHODZENIE JEST ZATWIERDZONE Z DEFINICJI. Zasób spod adresu
 * samej aplikacji nie jest „obcym serwerem" — to plik, który i tak
 * wyszedł z naszego wdrożenia. Bez tego wyjątku fixtury podglądu
 * musiałyby omijać walidator, czyli sprawdzałyby coś innego niż
 * produkcja. Wymóg HTTPS i lista hostów obowiązują wszystko poza
 * własnym origin (na produkcji własny origin i tak jest po HTTPS).
 */
export function powodOdrzuceniaUrl(url: string, bazowy?: string): string | null {
  const baza =
    bazowy ??
    (typeof document !== 'undefined' && document.baseURI
      ? document.baseURI
      : undefined);
  let u: URL;
  try {
    u = baza ? new URL(url, baza) : new URL(url);
  } catch {
    return 'adres nie jest poprawnym URL';
  }
  if (u.protocol === 'data:') {
    return 'adresy data: są niedozwolone';
  }
  let wlasnePochodzenie = false;
  try {
    wlasnePochodzenie = !!baza && u.origin === new URL(baza).origin;
  } catch {
    wlasnePochodzenie = false;
  }
  if (!wlasnePochodzenie) {
    if (u.protocol !== 'https:') {
      return 'wymagane HTTPS, otrzymano ' + u.protocol;
    }
    const host = u.hostname.toLowerCase();
    const zaufany = DOZWOLONE_ZRODLA.some(
      (z) => host === z || host.endsWith('.' + z),
    );
    if (!zaufany) {
      return 'pochodzenie spoza listy: ' + host;
    }
  }
  const sciezka = u.pathname.toLowerCase();
  if (!DOZWOLONE_FORMATY.some((f) => sciezka.endsWith(f))) {
    return 'format spoza listy: ' + sciezka.slice(sciezka.lastIndexOf('.'));
  }
  return null;
}

interface KontekstBledow {
  powody: string[];
  przyciete: number;
}

/** Wartość liczbowa przycięta do zakresu; `null` gdy w ogóle nie liczba. */
function liczbaWZakresie(
  x: unknown,
  klucz: keyof typeof ZAKRESY,
  ctx: KontekstBledow,
): number {
  const zakres = ZAKRESY[klucz];
  if (typeof x !== 'number' || !Number.isFinite(x)) {
    return zakres.domyslnie;
  }
  if (x < zakres.min) {
    ctx.przyciete++;
    ctx.powody.push(klucz + ' ' + x + ' < ' + zakres.min + ' — przycięte');
    return zakres.min;
  }
  if (x > zakres.max) {
    ctx.przyciete++;
    ctx.powody.push(klucz + ' ' + x + ' > ' + zakres.max + ' — przycięte');
    return zakres.max;
  }
  return x;
}

function walidujZasob(
  x: unknown,
  ctx: KontekstBledow,
): { zasob: Zasob; rodzaj: RodzajZasobu } | null {
  if (!jestObiektem(x)) {
    ctx.powody.push('zasób nie jest obiektem');
    return null;
  }
  const rodzajSurowy = tekst(x['type'] ?? x['rodzaj']);
  if (
    !rodzajSurowy ||
    !(RODZAJE_ZASOBU as readonly string[]).includes(rodzajSurowy)
  ) {
    ctx.powody.push('nieznany rodzaj zasobu: ' + rodzajSurowy);
    return null;
  }
  const rodzaj = rodzajSurowy as RodzajZasobu;
  const id = tekst(x['id']) ?? tekst(x['assetId']) ?? null;
  if (!id) {
    ctx.powody.push('zasób bez identyfikatora');
    return null;
  }
  const bajtowSurowe = x['bytes'] ?? x['size'] ?? x['bajtow'];
  const bajtow =
    typeof bajtowSurowe === 'number' && Number.isFinite(bajtowSurowe)
      ? bajtowSurowe
      : undefined;
  if (bajtow !== undefined && bajtow > MAX_BAJTOW_ZASOBU) {
    ctx.powody.push(
      'zasób ' + id + ' deklaruje ' + bajtow + ' B > ' + MAX_BAJTOW_ZASOBU,
    );
    return null;
  }

  // `CSS_PRESET_DECORATION` rysuje się samym stylem i NIE MA adresu.
  // Traktowanie go jak obrazka wymagałoby pustego `src`, czyli żądania
  // pod adres bieżącej strony — dlatego rozdzielamy te ścieżki tutaj.
  if (rodzaj === 'CSS_PRESET_DECORATION') {
    return {
      rodzaj,
      zasob: { id, rodzaj, url: '', opis: tekst(x['alt']) ?? undefined, bajtow },
    };
  }

  const url = tekst(x['url']) ?? tekst(x['src']);
  if (!url) {
    ctx.powody.push('zasób ' + id + ' bez adresu');
    return null;
  }
  const powod = powodOdrzuceniaUrl(url);
  if (powod) {
    ctx.powody.push('zasób ' + id + ': ' + powod);
    return null;
  }
  // BLIŹNIAK STATYCZNY (silnik 2): te same reguły co `url`. Podany, ale
  // nieprawidłowy = zasób odrzucony (fail-closed) — nie „połowa zasobu".
  const statycznySurowy = x['staticUrl'] ?? x['urlStatyczny'];
  let urlStatyczny: string | undefined;
  if (statycznySurowy !== undefined && statycznySurowy !== null) {
    const s = tekst(statycznySurowy);
    const powodS = s ? powodOdrzuceniaUrl(s) : 'pusty adres';
    if (powodS || !s) {
      ctx.powody.push('zasób ' + id + ' (staticUrl): ' + powodS);
      return null;
    }
    urlStatyczny = s;
  }
  return {
    rodzaj,
    zasob: {
      id,
      rodzaj,
      url,
      opis: tekst(x['alt']) ?? undefined,
      bajtow,
      ...(urlStatyczny ? { urlStatyczny } : {}),
    },
  };
}

function walidujDekoracje(
  x: unknown,
  zasoby: ReadonlyMap<string, Zasob>,
  ctx: KontekstBledow,
): Dekoracja | null {
  if (!jestObiektem(x)) {
    ctx.powody.push('dekoracja nie jest obiektem');
    return null;
  }
  const miejsce = tekst(x['placement'] ?? x['miejsce']);
  if (!miejsce || !(MIEJSCA as readonly string[]).includes(miejsce)) {
    ctx.powody.push('nieznane miejsce: ' + miejsce);
    return null;
  }
  const idZasobu = tekst(x['assetId'] ?? x['asset'] ?? x['zasob']);
  if (!idZasobu) {
    ctx.powody.push('dekoracja w ' + miejsce + ' bez wskazania zasobu');
    return null;
  }
  const zasob = zasoby.get(idZasobu);
  if (!zasob) {
    ctx.powody.push(
      'dekoracja w ' + miejsce + ' wskazuje nieistniejący zasób ' + idZasobu,
    );
    return null;
  }

  const presetSurowy =
    tekst(x['animationPreset'] ?? x['preset'] ?? x['effect'] ?? x['type']) ??
    'NONE';
  if (!(PRESETY as readonly string[]).includes(presetSurowy)) {
    ctx.powody.push('nieznany efekt ' + presetSurowy);
    return null;
  }
  const preset = presetSurowy as Preset;

  const p = jestObiektem(x['params'])
    ? (x['params'] as Record<string, unknown>)
    : jestObiektem(x['parametry'])
      ? (x['parametry'] as Record<string, unknown>)
      : {};

  const limitMiejsca = OGRANICZENIA[miejsce as Miejsce].maxElementow;
  const density = Math.min(liczbaWZakresie(p['density'], 'density', ctx), limitMiejsca);
  const maxItems = Math.min(
    liczbaWZakresie(p['maxItems'], 'maxItems', ctx),
    limitMiejsca,
  );

  return {
    miejsce: miejsce as Miejsce,
    zasob,
    preset,
    parametry: {
      speed: liczbaWZakresie(p['speed'], 'speed', ctx),
      delay: liczbaWZakresie(p['delay'], 'delay', ctx),
      density,
      opacity: liczbaWZakresie(p['opacity'], 'opacity', ctx),
      scale: liczbaWZakresie(p['scale'], 'scale', ctx),
      rotation: liczbaWZakresie(p['rotation'], 'rotation', ctx),
      maxItems,
    },
  };
}

/**
 * CACHE BUSTING PO STRONIE KLIENTA (OWNER 2026-09-29, pkt 4).
 *
 * Do adresu każdego zasobu dopisujemy wersję motywu (`tv=<themeVersion>`).
 * Backend i tak publikuje pliki pod adresami z hashem treści (niezmienne),
 * ale gdyby kiedyś podmienił plik pod TYM SAMYM adresem i podbił wersję,
 * aplikacja i tak pobierze nowy plik — pamięć WebView nie może zatrzymać
 * starej grafiki. Nowy adres = nowy element w szablonie (`kluczWidoku`).
 */
export function zWersjaMotywu(zasob: Zasob, wersja: number): Zasob {
  if (!zasob.url) return zasob;
  const zWersja = (adres: string): string => {
    const [bezKotwicy] = adres.split('#');
    const znak = bezKotwicy.includes('?') ? '&' : '?';
    return bezKotwicy + znak + 'tv=' + wersja;
  };
  // Bliźniak statyczny (silnik 2) dostaje TĘ SAMĄ wersję — inaczej po zmianie
  // motywu spoczynek epizodu pokazałby z pamięci podręcznej starą scenę.
  return zasob.urlStatyczny
    ? { ...zasob, url: zWersja(zasob.url), urlStatyczny: zWersja(zasob.urlStatyczny) }
    : { ...zasob, url: zWersja(zasob.url) };
}

/**
 * Główne wejście. Przyjmuje cokolwiek, oddaje motyw albo `null`.
 *
 * Odpowiedź może przyjść opakowana (`{ object: ... }` jak reszta API tej
 * aplikacji) albo płasko — sprawdzamy obie postacie, bo kontrakt backendu
 * nie jest jeszcze ustalony i nie chcemy przywiązywać klienta do jednej.
 */
export function walidujMotyw(surowe: unknown): WynikWalidacji {
  const ctx: KontekstBledow = { powody: [], przyciete: 0 };
  const pusty: RaportWalidacji = {
    odrzuconeDekoracje: 0,
    przycieteParametry: 0,
    powody: ctx.powody,
  };

  let zrodlo: unknown = surowe;
  if (jestObiektem(zrodlo) && 'object' in zrodlo) {
    zrodlo = (zrodlo as Record<string, unknown>)['object'];
  }
  if (jestObiektem(zrodlo) && 'theme' in zrodlo) {
    zrodlo = (zrodlo as Record<string, unknown>)['theme'];
  }
  if (!jestObiektem(zrodlo)) {
    ctx.powody.push('odpowiedź nie jest obiektem');
    return { motyw: null, raport: { ...pusty, powody: ctx.powody } };
  }

  const id = tekst(zrodlo['themeId'] ?? zrodlo['id']);
  if (!id) {
    ctx.powody.push('brak identyfikatora motywu');
    return { motyw: null, raport: { ...pusty, powody: ctx.powody } };
  }

  // Odrzucenie CAŁEGO motywu — jedna ścieżka dla każdego powodu.
  const odrzuc = (powod: string, odrzucone = 0): WynikWalidacji => {
    ctx.powody.push(powod);
    return {
      motyw: null,
      raport: {
        odrzuconeDekoracje: odrzucone,
        przycieteParametry: ctx.przyciete,
        powody: ctx.powody,
      },
    };
  };
  const jest = (k: string): boolean =>
    zrodlo[k] !== undefined && zrodlo[k] !== null;

  // ── ZGODNOŚĆ (pkt 7): silnik i wymagane zdolności ────────────────────
  const zgodnosc = jestObiektem(zrodlo['compatibility'])
    ? (zrodlo['compatibility'] as Record<string, unknown>)
    : {};
  const minSilnik = zgodnosc['minEngineVersion'] ?? zrodlo['minEngineVersion'];
  if (minSilnik !== undefined && minSilnik !== null) {
    if (typeof minSilnik !== 'number' || !Number.isFinite(minSilnik)) {
      return odrzuc('minEngineVersion nie jest liczbą');
    }
    if (minSilnik > WERSJA_SILNIKA) {
      return odrzuc(
        'motyw wymaga silnika ' + minSilnik + ', aplikacja ma ' + WERSJA_SILNIKA,
      );
    }
  }
  const minUniwersalny = zgodnosc['minUniversalEngine'];
  if (minUniwersalny !== undefined && minUniwersalny !== null) {
    if (typeof minUniwersalny !== 'number' || !Number.isFinite(minUniwersalny)) {
      return odrzuc('minUniversalEngine nie jest liczbą');
    }
    if (minUniwersalny > UNIWERSALNY_SILNIK) {
      return odrzuc('motyw wymaga uniwersalnego silnika ' + minUniwersalny + ', aplikacja ma ' + UNIWERSALNY_SILNIK);
    }
  }
  const wymaga = zgodnosc['requires'] ?? zrodlo['requires'];
  if (wymaga !== undefined && wymaga !== null) {
    if (!Array.isArray(wymaga)) return odrzuc('requires nie jest listą');
    const nieznane = wymaga.filter(
      (z) => typeof z !== 'string' || !ZDOLNOSCI_SILNIKA.includes(z),
    );
    if (nieznane.length) {
      return odrzuc('nieznane zdolności: ' + nieznane.join(', '));
    }
  }
  if (jest('fallback')) {
    const f = zrodlo['fallback'];
    const tryb = tekst(jestObiektem(f) ? (f['mode'] ?? f['tryb']) : f);
    if (!tryb || tryb.toUpperCase() !== STRATEGIA_AWARYJNA) {
      return odrzuc('nieobsługiwana strategia awaryjna: ' + tryb);
    }
  }

  const stanSurowy = tekst(zrodlo['state'] ?? zrodlo['stan']);
  if (stanSurowy && !(STANY_MOTYWU as readonly string[]).includes(stanSurowy)) {
    return odrzuc('nieznany stan motywu: ' + stanSurowy);
  }
  const stan: StanMotywu = (stanSurowy as StanMotywu | null) ?? 'ACTIVE';

  const aktywnySurowy =
    zrodlo['enabled'] ?? zrodlo['active'] ?? zrodlo['aktywny'];
  const aktywny = typeof aktywnySurowy === 'boolean' ? aktywnySurowy : false;

  const rewizjaSurowa =
    zrodlo['themeVersion'] ?? zrodlo['revision'] ?? zrodlo['rewizja'];
  const rewizja =
    typeof rewizjaSurowa === 'number' && Number.isFinite(rewizjaSurowa)
      ? Math.trunc(rewizjaSurowa)
      : 0;

  // ── ZASOBY: każdy musi przejść, inaczej cały motyw odpada ────────────
  const zasoby = new Map<string, Zasob>();
  if (jest('assets') && !Array.isArray(zrodlo['assets'])) {
    return odrzuc('assets nie jest listą');
  }
  const listaZasobow = Array.isArray(zrodlo['assets'])
    ? (zrodlo['assets'] as unknown[])
    : [];
  if (listaZasobow.length > MAX_ZASOBOW) {
    return odrzuc('zasobów ' + listaZasobow.length + ' > limitu ' + MAX_ZASOBOW);
  }
  const schemat3 = (typeof zrodlo['schemaVersion'] === 'number' && (zrodlo['schemaVersion'] as number) >= WERSJA_SCHEMATU)
    || jest('scenes') || jest('layers');
  for (const surowyZasob of listaZasobow) {
    // Schemat 3: rodzaj zasobu jest opcjonalny — `animated: true` = plik sam
    // się animuje (SVG z CSS w środku, GIF), domyślnie obraz statyczny.
    const surowy = schemat3 && jestObiektem(surowyZasob) && surowyZasob['type'] === undefined
      ? { ...surowyZasob, type: surowyZasob['animated'] === true ? 'LIGHTWEIGHT_VECTOR_ANIMATION' : 'STATIC_IMAGE' }
      : surowyZasob;
    const wynik = walidujZasob(surowy, ctx);
    if (!wynik) return odrzuc('zasób odrzucony — motyw w całości wyłączony');
    if (zasoby.has(wynik.zasob.id)) {
      return odrzuc('powtórzony identyfikator zasobu ' + wynik.zasob.id);
    }
    zasoby.set(wynik.zasob.id, zWersjaMotywu(wynik.zasob, rewizja));
  }

  // ── DEKORACJE: `placements` + skróty kontraktu `hero` i `effects` ────
  if (jest('placements') && !Array.isArray(zrodlo['placements'])) {
    return odrzuc('placements nie jest listą');
  }
  const listaDekoracji: unknown[] = Array.isArray(zrodlo['placements'])
    ? [...(zrodlo['placements'] as unknown[])]
    : [];
  if (jest('hero')) {
    const h = zrodlo['hero'];
    if (!jestObiektem(h)) return odrzuc('hero nie jest obiektem');
    listaDekoracji.push({
      placement: 'EVENT_HERO',
      assetId: h['assetId'] ?? h['asset'],
      animationPreset: h['animationPreset'] ?? h['effect'] ?? 'NONE',
      params: h['params'],
    });
  }
  if (jest('effects')) {
    const e = zrodlo['effects'];
    if (!Array.isArray(e)) return odrzuc('effects nie jest listą');
    for (const ef of e) {
      if (!jestObiektem(ef)) return odrzuc('efekt nie jest obiektem');
      const rodzajEfektu = tekst(ef['type'] ?? ef['effect']);
      const domyslneMiejsce =
        rodzajEfektu === 'FALLING' || rodzajEfektu === 'SNOW'
          ? 'HOME_BACKGROUND_OVERLAY'
          : undefined;
      listaDekoracji.push({
        placement: ef['placement'] ?? domyslneMiejsce,
        assetId: ef['assetId'] ?? ef['asset'],
        animationPreset: rodzajEfektu,
        params: ef['params'],
      });
    }
  }
  if (listaDekoracji.length > MAX_DEKORACJI) {
    return odrzuc(
      'dekoracji ' + listaDekoracji.length + ' > limitu ' + MAX_DEKORACJI,
      listaDekoracji.length,
    );
  }
  const dekoracje: Dekoracja[] = [];
  const zajete = new Set<Miejsce>();
  for (const surowa of listaDekoracji) {
    const d = walidujDekoracje(surowa, zasoby, ctx);
    if (!d) return odrzuc('dekoracja odrzucona — motyw w całości wyłączony', 1);
    // Jedno miejsce, jedna dekoracja — druga to błąd konfiguracji.
    if (zajete.has(d.miejsce)) {
      return odrzuc('miejsce ' + d.miejsce + ' zajęte dwa razy', 1);
    }
    zajete.add(d.miejsce);
    dekoracje.push(d);
  }

  // ── POLA OKAZJI: podane, ale złe = cały motyw odpada ─────────────────
  const pasekSurowy = zrodlo['messageBar'] ?? zrodlo['pasek'];
  const pasekUkryty = jestObiektem(pasekSurowy) && pasekSurowy['visible'] === false;
  const pasek = pasekUkryty ? null : walidujPasek(pasekSurowy, zasoby);
  if ((jest('messageBar') || jest('pasek')) && !pasek && !pasekUkryty) {
    return odrzuc('messageBar nieprawidłowy');
  }

  // ── GRAF SCENY: schemat 3 wprost, silnik 1/2 przez adapter ───────────
  let scena: Scena;
  if (schemat3) {
    if (dekoracje.length) return odrzuc('schemat 3 nie łączy się z placements/hero/effects silnika 1/2');
    const ctxSceny: KontekstSceny = { przyciete: 0, powody: ctx.powody };
    const ws = walidujScene(zrodlo, zasoby, ctxSceny);
    ctx.przyciete += ctxSceny.przyciete;
    if (!ws.scena) return odrzuc('scena nieprawidłowa: ' + ws.powod);
    scena = ws.scena;
  } else {
    scena = scenaZDekoracji(dekoracje);
  }
  const typSurowy = zrodlo['typography'] ?? zrodlo['typografia'];
  const typografia = walidujTypografie(typSurowy);
  if (
    typSurowy !== undefined &&
    typSurowy !== null &&
    !typografia &&
    (tekst(jestObiektem(typSurowy) ? (typSurowy['textTransform'] ?? typSurowy['transform']) : typSurowy) ?? '').toUpperCase() !== 'NONE'
  ) {
    return odrzuc('typography nieobsługiwane');
  }
  const paleta = walidujPalete(zrodlo['palette'] ?? zrodlo['paleta']);
  if ((jest('palette') || jest('paleta')) && !paleta) {
    return odrzuc('palette nieprawidłowa albo nieczytelna (kontrast)');
  }
  const odrzucone = 0;

  const motyw: MotywSezonowy = {
    aktywny,
    id,
    nazwa: tekst(zrodlo['name'] ?? zrodlo['nazwa']) ?? id,
    stan,
    odKiedy: czasNaMs(zrodlo['startsAt'] ?? zrodlo['odKiedy']),
    doKiedy: czasNaMs(zrodlo['endsAt'] ?? zrodlo['doKiedy']),
    rewizja,
    zaktualizowano: czasNaMs(zrodlo['updatedAt'] ?? zrodlo['zaktualizowano']),
    dekoracje,
    pasek,
    typografia,
    paleta,
    scena,
  };

  return {
    motyw,
    raport: {
      odrzuconeDekoracje: odrzucone,
      przycieteParametry: ctx.przyciete,
      powody: ctx.powody,
    },
  };
}
